import {
  attachmentsCreateSignedUrlCreateMutation,
  CountriesListData,
  countriesListInfiniteOptions,
  CountryReadable,
  invoicesSettingsCreateMutation,
  invoicesSettingsCreateQueryKey,
  invoicesSettingsRetrieveOptions,
  invoicesSettingsRetrieveQueryKey,
  PaginatedCountryListReadable
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { SettingsDynamicFields } from '@crm/features/billing/components'
import { useStyles } from '@crm/features/billing/styles'
import { useDefaultCurrency } from '@crm/hooks/useDefaultCurrency'
import { onSubmitUploadDocument, validatePhoto } from '@crm/utils'
import { Avatar } from '@shared/components'

import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'

import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  message,
  notification,
  Row,
  Space,
  Typography,
  Upload
} from 'antd'
import { RcFile } from 'antd/es/upload'
import { UploadFile } from 'antd/lib'
import { UploadChangeParam } from 'antd/lib/upload'
import { Image } from 'iconsax-react'
import { useEffect, useState } from 'react'

type SettingsFormProps = {
  formName: string
  onChange?: (src?: string | null | UploadFile) => void
}

const { Text, Title } = Typography

export const SettingsForm = ({ formName, onChange }: SettingsFormProps) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { styles } = useStyles()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { defaultCurrency } = useDefaultCurrency()

  const [companyLogo, setCompanyLogo] = useState<string | null>()
  const [file, setFile] = useState<UploadFile | null>(null)

  const { data } = useQuery(invoicesSettingsRetrieveOptions())

  useEffect(() => {
    if (data) {
      const { attachment, ...rest } = data
      form.setFieldsValue({ ...rest })
      setCompanyLogo(attachment.file)
    } else {
      form.setFieldsValue({
        bank_accounts: [{ currency: defaultCurrency }]
      })
    }
  }, [data, form, defaultCurrency])

  const { mutate: createAttachmentMutate } = useMutation({
    ...attachmentsCreateSignedUrlCreateMutation(),
    onError: (error) => showApiErrors(error, form),
    onSuccess: async (data) => {
      const signedUrl = data?.upload_url
      const fileType = file?.type

      form?.setFieldValue('attachment', data?.id)
      await onSubmitUploadDocument(signedUrl, file as RcFile, fileType)
      invalidateQueries({
        invoicesSettingsRetrieveQueryKey,
        invoicesSettingsCreateQueryKey
      })
    }
  })

  const { mutate } = useMutation({
    ...invoicesSettingsCreateMutation(),
    onError: (error) => showApiErrors(error, form),
    onSuccess: () => {
      invalidateQueries({
        invoicesSettingsRetrieveQueryKey,
        invoicesSettingsCreateQueryKey
      })
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({
      body: {
        ...values,
        attachment: values.attachment,
        id: data?.id
      }
    })
  }

  const onRemoveLogo = () => {
    onChange?.(null)
    setCompanyLogo(null)
  }

  const updatePhotoState = async (file: RcFile) => {
    try {
      await validatePhoto(file)

      setFile(file)

      createAttachmentMutate({
        body: {
          title: file.name
        }
      })

      return true
    } catch (e) {
      const error = e as string
      message.error(t(error))
      return Upload.LIST_IGNORE
    }
  }

  const handleChange = ({
    file
  }: UploadChangeParam<UploadFile<{ url: string }>>) => {
    if (file.originFileObj) {
      const previewUrl = URL.createObjectURL(file.originFileObj as RcFile)
      onChange?.(previewUrl)
      setCompanyLogo(previewUrl)
    } else {
      onChange?.(file)
    }
  }

  return (
    <Card className={styles.card}>
      <Form name={formName} form={form} onFinish={onSubmit} layout="vertical">
        <Title level={5}>{t('billing.logo')}</Title>

        <Space size="large" align="center">
          <Avatar
            avatarProps={{
              size: 100,
              ...(companyLogo
                ? { src: companyLogo }
                : { icon: <Image size={64} /> })
            }}
          />

          <Space direction="vertical" align="start">
            <Flex gap={8}>
              <Form.Item name="attachment">
                <Upload
                  customRequest={() => null}
                  beforeUpload={updatePhotoState}
                  onChange={handleChange}
                  showUploadList={false}
                  accept="image/png, image/jpeg"
                >
                  <Button>{t('buttons.update')}</Button>
                </Upload>
              </Form.Item>

              {!!companyLogo && (
                <Button danger type="text" onClick={onRemoveLogo}>
                  {t('buttons.remove')}
                </Button>
              )}
            </Flex>
            <Text strong>{t('billing.max_upload_image')}</Text>
          </Space>
        </Space>

        <Divider />

        <Title level={5}>{t('billing.general_information')}</Title>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={t('billing.company_name')} name="name">
              <Input type="text" placeholder={t('billing.name')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={t('billing.email')} name="email">
              <Input type="email" placeholder={t('billing.email')} />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Title level={5}>{t('billing.address')}</Title>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label={t('billing.country')} name="country">
              <InfiniteSelect<
                CountriesListData,
                PaginatedCountryListReadable,
                CountryReadable
              >
                getQueryFunction={countriesListInfiniteOptions}
                placeholder={t('billing.country')}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={t('billing.state')} name="state">
              <Input placeholder={t('billing.state')} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={t('billing.city')} name="city">
              <Input type="text" placeholder={t('billing.city')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={16}>
            <Form.Item label={t('billing.address')} name="address">
              <Input placeholder={t('billing.address')} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={t('billing.postal_code')} name="postal_code">
              <Input placeholder={t('billing.postal_code')} />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Title level={5}>{t('billing.bank_information')}</Title>

        <SettingsDynamicFields />
      </Form>
    </Card>
  )
}
